/**
 * PageMeta - SEO and social media metadata component
 *
 * Provides per-page <title>, meta description, OpenGraph tags, and canonical URLs
 * using React 19 native document metadata hoisting (no library needed).
 *
 * @example
 * <PageMeta
 *   title="Services"
 *   description="Strategic consulting services for small businesses"
 *   path="/services"
 * />
 */

import { useEffect } from 'react';

export interface PageMetaProps {
  /** Page title - will be formatted as "Title | SmallBiz Innovation" */
  title: string;
  /** Meta description for SEO (recommended: 150-160 characters) */
  description: string;
  /** URL path for canonical and OG URL (e.g., "/services") */
  path: string;
  /** OpenGraph image URL (defaults to site default) */
  ogImage?: string;
  /** OpenGraph type (defaults to "website") */
  ogType?: 'website' | 'article';
  /** Article publish date (for og:type="article") */
  publishedTime?: string;
  /** Article modified date (for og:type="article") */
  modifiedTime?: string;
  /** Article author name */
  author?: string;
  /** Prevent indexing of this page */
  noIndex?: boolean;
  /** Additional keywords for meta keywords tag */
  keywords?: string[];
}

const SITE_NAME = 'SmallBiz Innovation';
const BASE_URL = 'https://www.smallbizinnovation.com';
const DEFAULT_OG_IMAGE = 'https://images.smallbizinnovation.com/og/v2/page/default.png';
const TWITTER_HANDLE = '@smallbizinnov';

/**
 * #534 — Module-level "first paint" guard for the edge/client <head> hand-off.
 *
 * The Cloudflare Worker (lib/meta-injection.ts) injects the tuned, crawler-facing
 * <head> into the INITIAL HTML at the edge — the single source of truth for SEO.
 * PageMeta must NOT overwrite that on the first client render (doing so reverted
 * the edge's tuned tags to the component's drifted props). It only owns the head
 * on SUBSEQUENT SPA navigations, where there is no fresh edge injection.
 */
let clientHasHydrated = false;

/**
 * Returns true exactly once per page load — on the FIRST call, i.e. the initial
 * (edge-injected) paint — and false on every subsequent call (SPA navigations).
 * Exported so the guard can be unit-tested in isolation from the DOM.
 */
export function consumeInitialPaint(): boolean {
  if (clientHasHydrated) return false;
  clientHasHydrated = true;
  return true;
}

/** Test-only: reset the module-level first-paint guard between cases. */
export function resetInitialPaintGuardForTests(): void {
  clientHasHydrated = false;
}

export function PageMeta({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
  keywords = [],
}: PageMetaProps) {
  const SITE_SUFFIX = ` | ${SITE_NAME}`;
  const sanitizedTitle = title.endsWith(SITE_SUFFIX) ? title.slice(0, -SITE_SUFFIX.length) : title;
  const fullTitle = sanitizedTitle === 'Home' ? SITE_NAME : `${sanitizedTitle} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;

  // Truncate description to 160 chars for SEO best practices
  const truncatedDescription =
    description.length > 160 ? `${description.slice(0, 157)}...` : description;

  useEffect(() => {
    const ensureMetaByName = (name: string) => {
      let meta = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      return meta;
    };

    const ensureMetaByProperty = (property: string) => {
      let meta = document.head.querySelector(
        `meta[property="${property}"]`,
      ) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      return meta;
    };

    const ensureCanonical = () => {
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      return link;
    };

    // #534 — On the FIRST client paint the edge-injected <head> is already the
    // correct, tuned, crawler-facing source of truth, so we must NOT overwrite
    // it. We write tags ONLY on subsequent SPA navigations (route changes with
    // no full reload, hence no fresh edge injection). `consumeInitialPaint()`
    // returns true exactly once per page load — for that very first mount.
    const isInitialPaint = consumeInitialPaint();

    if (!isInitialPaint) {
      document.title = fullTitle;
      ensureMetaByName('description').setAttribute('content', truncatedDescription);
      ensureCanonical().setAttribute('href', canonicalUrl);

      if (noIndex) {
        ensureMetaByName('robots').setAttribute('content', 'noindex, nofollow');
      }

      if (keywords.length > 0) {
        ensureMetaByName('keywords').setAttribute('content', keywords.join(', '));
      }

      ensureMetaByProperty('og:type').setAttribute('content', ogType);
      ensureMetaByProperty('og:url').setAttribute('content', canonicalUrl);
      ensureMetaByProperty('og:title').setAttribute('content', fullTitle);
      ensureMetaByProperty('og:description').setAttribute('content', truncatedDescription);
      ensureMetaByProperty('og:image').setAttribute('content', ogImage);
      ensureMetaByProperty('og:site_name').setAttribute('content', SITE_NAME);
      ensureMetaByProperty('og:locale').setAttribute('content', 'en_US');

      if (ogType === 'article' && publishedTime) {
        ensureMetaByProperty('article:published_time').setAttribute('content', publishedTime);
      }
      if (ogType === 'article' && modifiedTime) {
        ensureMetaByProperty('article:modified_time').setAttribute('content', modifiedTime);
      }
      if (ogType === 'article' && author) {
        ensureMetaByProperty('article:author').setAttribute('content', author);
      }

      ensureMetaByName('twitter:card').setAttribute('content', 'summary_large_image');
      ensureMetaByName('twitter:site').setAttribute('content', TWITTER_HANDLE);
      ensureMetaByName('twitter:title').setAttribute('content', fullTitle);
      ensureMetaByName('twitter:description').setAttribute('content', truncatedDescription);
      ensureMetaByName('twitter:image').setAttribute('content', ogImage);
      ensureMetaByName('author').setAttribute('content', author || SITE_NAME);
    }

    // #887 — third-party title optimizers (nytsys) overwrite document.title
    // after load with a stale cached value (e.g. "Something Went Wrong" left
    // over from a prior error-boundary render before the #878/#879/#880 crash
    // fixes). Re-assert the AUTHORITATIVE title + og:/twitter: title whenever the
    // <title> is mutated to a different value, for a short window after mount.
    // On the first paint the authoritative title is the edge-injected title
    // already in the DOM — re-asserting the component prop here would be the
    // exact #534 revert we removed; on a navigation it is the fullTitle we just
    // wrote. The observer fires only when a third party mutates the title, so it
    // never proactively overwrites the edge head. It self-disconnects after 6s.
    const authoritativeTitle = isInitialPaint ? document.title : fullTitle;
    const reassertTitle = () => {
      if (document.title !== authoritativeTitle) document.title = authoritativeTitle;
      ensureMetaByProperty('og:title').setAttribute('content', authoritativeTitle);
      ensureMetaByName('twitter:title').setAttribute('content', authoritativeTitle);
    };

    const titleEl = document.head.querySelector('title');
    let titleObserver: MutationObserver | null = null;
    if (titleEl && typeof MutationObserver !== 'undefined') {
      titleObserver = new MutationObserver(() => {
        if (document.title !== authoritativeTitle) reassertTitle();
      });
      titleObserver.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }
    const stopObserving = window.setTimeout(() => titleObserver?.disconnect(), 6000);

    return () => {
      titleObserver?.disconnect();
      window.clearTimeout(stopObserving);
    };
  }, [
    author,
    canonicalUrl,
    fullTitle,
    keywords,
    modifiedTime,
    noIndex,
    ogImage,
    ogType,
    publishedTime,
    truncatedDescription,
  ]);

  return null;
}

export default PageMeta;
